import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ThumbsUp, CheckCircle2, Reply as ReplyIcon, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

function PostCard(props) {
    const { post, isReply, replyOpen, onToggleReply, onUpvote, onResolve, user } = props;
    const isInstructor = post.user_role === "instructor" || post.user_role === "admin";
    return (
        <div
            className={`p-4 border border-border rounded-sm bg-card ${
                isInstructor ? "ring-1 ring-accent/30" : ""
            }`}
        >
            <div className="flex items-center gap-2 mb-2 text-sm flex-wrap">
                <span className="font-medium text-foreground">{post.user_name}</span>
                <Badge
                    variant="outline"
                    className={`capitalize text-xs ${isInstructor ? "border-accent text-accent" : ""}`}
                >
                    {post.user_role}
                </Badge>
                {post.resolved && (
                    <Badge className="bg-accent/20 text-accent border border-accent/40 text-xs">
                        Resolved
                    </Badge>
                )}
                <span className="text-xs text-muted-foreground ml-auto">
                    {new Date(post.created_at).toLocaleDateString()}
                </span>
            </div>
            <div className="text-sm text-foreground/85 whitespace-pre-wrap">{post.body}</div>
            <div className="mt-3 flex items-center gap-1 flex-wrap">
                <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 gap-1"
                    onClick={() => onUpvote(post.id)}
                    data-testid={`upvote-${post.id}`}
                >
                    <ThumbsUp className="h-3 w-3" />
                    {post.upvotes?.length || 0}
                </Button>
                {!isReply && (
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1"
                        onClick={onToggleReply}
                        data-testid={`reply-toggle-${post.id}`}
                    >
                        {replyOpen ? (
                            <>
                                <X className="h-3 w-3" />
                                Cancel
                            </>
                        ) : (
                            <>
                                <ReplyIcon className="h-3 w-3" />
                                Reply
                            </>
                        )}
                    </Button>
                )}
                {(user?.role === "instructor" || user?.role === "admin") && !post.resolved && (
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1"
                        onClick={() => onResolve(post.id)}
                        data-testid={`resolve-${post.id}`}
                    >
                        <CheckCircle2 className="h-3 w-3" />
                        Mark resolved
                    </Button>
                )}
            </div>
        </div>
    );
}

export default function Discussion({ lessonId }) {
    const [posts, setPosts] = useState([]);
    const [body, setBody] = useState("");
    const [loading, setLoading] = useState(true);
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyBody, setReplyBody] = useState("");
    const { user } = useAuth();

    const load = async () => {
        const { data } = await api.get(`/lessons/${lessonId}/discussion`);
        setPosts(data);
        setLoading(false);
    };

    useEffect(() => {
        setLoading(true);
        setReplyingTo(null);
        load();
        // eslint-disable-next-line
    }, [lessonId]);

    const handlePost = async () => {
        if (!body.trim()) return;
        try {
            await api.post(`/lessons/${lessonId}/discussion`, { body });
            setBody("");
            load();
        } catch {
            toast.error("Could not post");
        }
    };

    const handleReply = async (parentId) => {
        if (!replyBody.trim()) return;
        try {
            await api.post(`/lessons/${lessonId}/discussion`, {
                body: replyBody,
                parent_id: parentId,
            });
            setReplyBody("");
            setReplyingTo(null);
            load();
        } catch {
            toast.error("Could not reply");
        }
    };

    const toggleUp = async (id) => {
        await api.post(`/discussion/${id}/upvote`);
        load();
    };

    const resolve = async (id) => {
        await api.post(`/discussion/${id}/resolve`);
        load();
    };

    const { topLevel, repliesByParent } = useMemo(() => {
        const top = [];
        const map = {};
        for (const p of posts) {
            if (p.parent_id) {
                (map[p.parent_id] = map[p.parent_id] || []).push(p);
            } else {
                top.push(p);
            }
        }
        return { topLevel: top, repliesByParent: map };
    }, [posts]);

    return (
        <div className="space-y-4" data-testid={`discussion-${lessonId}`}>
            <div>
                <Textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Ask a question or share an insight…"
                    rows={3}
                    data-testid="discussion-body-input"
                />
                <div className="mt-2 flex justify-end">
                    <Button
                        size="sm"
                        onClick={handlePost}
                        disabled={!body.trim()}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="discussion-post-btn"
                    >
                        Post
                    </Button>
                </div>
            </div>
            {loading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
            ) : topLevel.length === 0 ? (
                <div className="text-sm text-muted-foreground italic">
                    Be the first to start a discussion.
                </div>
            ) : (
                <ul className="space-y-3">
                    {topLevel.map((p) => {
                        const replies = repliesByParent[p.id] || [];
                        const replyOpen = replyingTo === p.id;
                        return (
                            <li key={p.id} data-testid={`post-${p.id}`}>
                                <PostCard
                                    post={p}
                                    isReply={false}
                                    replyOpen={replyOpen}
                                    onToggleReply={() =>
                                        setReplyingTo((cur) => (cur === p.id ? null : p.id))
                                    }
                                    onUpvote={toggleUp}
                                    onResolve={resolve}
                                    user={user}
                                />
                                {replyOpen && (
                                    <div className="mt-2 ml-6 md:ml-10">
                                        <Textarea
                                            rows={2}
                                            value={replyBody}
                                            onChange={(e) => setReplyBody(e.target.value)}
                                            placeholder={`Reply to ${p.user_name}…`}
                                            data-testid={`reply-input-${p.id}`}
                                        />
                                        <div className="mt-2 flex justify-end">
                                            <Button
                                                size="sm"
                                                onClick={() => handleReply(p.id)}
                                                disabled={!replyBody.trim()}
                                                className="bg-primary hover:bg-primary/90"
                                                data-testid={`reply-submit-${p.id}`}
                                            >
                                                Post reply
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                {replies.length > 0 && (
                                    <ul className="space-y-3 mt-3 ml-6 md:ml-10 border-l-2 border-accent/30 pl-4">
                                        {replies.map((r) => (
                                            <li key={r.id} data-testid={`post-${r.id}`}>
                                                <PostCard
                                                    post={r}
                                                    isReply={true}
                                                    replyOpen={false}
                                                    onToggleReply={() => {}}
                                                    onUpvote={toggleUp}
                                                    onResolve={resolve}
                                                    user={user}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
